import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router';
import PlatformActionsPageBlock from '../../../../components/PlatformComponents/PlatformActionsPage';
import {EPaymentRequiredType, EPlatformsNames, EStripeSubStatus, ETrialStatus} from '../../../../models/consts';
import {IError, IPaymentRequiredPoppup} from '../../../../models/inner-models';
import {
  downloadGitlabSnippetZip,
  downloadGitlabZip,
  getGitlabActions,
  restoreGitlabBackup,
  restoreGitlabSnippetsBackup
} from '../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {clearBackupsState} from '../../../../store/reducers/backups';
import {
  getGitlabActionsSelector,
  getGitlabActionsTotalSelector,
  getGitlabCanRestore,
  gitlabSubscription,
  gitlabTrialStatus,
  isGitlabActionsLoadingSelector,
  isSubUser,
  roleByApp,
} from '../../../../store/selectors';
import restore from '../../../../utils/functions/restore';
import withRedirectAfterLogout from '../../../../utils/withRedirectAfterLogout';
import {IBackupRow} from '../../types';
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import {errorBigIcon, iconNotifySuccess} from "../../../../assets/icons/icons";

const GitlabActivityPage: FunctionComponent = () => {
  const platformName = EPlatformsNames.GITLAB;

  const [searchParams] = useSearchParams();
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const id = searchParams.get('id');

  const gitlabActions = useAppSelector(getGitlabActionsSelector);
  const actionsTotal = useAppSelector(getGitlabActionsTotalSelector);
  const loading = useAppSelector(isGitlabActionsLoadingSelector);
  const canRestore = useAppSelector(getGitlabCanRestore);
  const trialState = useAppSelector(gitlabTrialStatus);
  const subInfo = useAppSelector(gitlabSubscription);
  const isUserSub = useAppSelector(isSubUser);
  const roleApp = useAppSelector(roleByApp);
  const isAdminRole = isUserSub && roleApp?.gitlab === 'admin' && (trialState === 'active' || subInfo?.planName === 'Pro') || !isUserSub

  const [rows, setRows] = useState<Array<IBackupRow> | null>(null);
  const [paymentRequiredState, setPaymentRequiredState] = useState<EPaymentRequiredType>(EPaymentRequiredType.NON);
  const [paymentPoppup, setPaymentPoppup] = useState<IPaymentRequiredPoppup>({
    isOpen: false,
    platformName: platformName,
  });
  const [restartFetch, setRestartFetch] = useState<number>(0);
  const isSnip = localStorage['gitlabType'] === 'snippet'
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (actionsTotal && actionsTotal !== total) {
      setTotal(actionsTotal)
    }
  }, [actionsTotal])

  useEffect(() => {
    if (!localStorage['gitlabType']) {
      navigate('/platform/dashboard/gitlab')
      return
    }
  }, [page, restartFetch])

  useEffect(() => {
    setPaymentRequiredState(
      subInfo?.status === EStripeSubStatus.ACTIVE || trialState === ETrialStatus.STARTED ?
        EPaymentRequiredType.NON :
        !subInfo ? EPaymentRequiredType.TRIAL : EPaymentRequiredType.SUB,
    );
  }, [trialState, subInfo]);

  const restoreItem = (versions: string, name: string, key: string, emails: string[]) => {
    if (paymentRequiredState === EPaymentRequiredType.NON) {
      return restore(() => onConfirmRestore(versions, name, key, emails), translate);
    } else {
      if (!isUserSub) {
        setPaymentPoppup((prev) => ({
          ...prev,
          isOpen: true,
        }));
      }
    }
  };

  const closeTrialPoppup = useCallback(() => {
    setPaymentPoppup({...paymentPoppup, isOpen: false});
  }, []);

  const onConfirmRestore = async (versionId, name, projectId, emails) => {
    if (versionId && projectId) {
      return dispatch(isSnip ? restoreGitlabSnippetsBackup({
        items: [{id: projectId, versionId: versionId, name: name}],
        emails
      }) : restoreGitlabBackup({items: [{id: projectId, versionId: versionId, name: name}], emails})).unwrap();
    }
  };

  const downloadZipCallback = useCallback(({versionid, key}) => {
    dispatch(isSnip ? downloadGitlabSnippetZip({
      snippetId: key,
      versionId: versionid
    }) : downloadGitlabZip({projectId: key, versionId: versionid}))
      .unwrap()
      .then((res) => {
        window.open(`${res.url}`, '_blank');
      })
      .catch((err) => {
        console.log(err);
        const error = err as IError;

        if (error.status === 'success') {
          setRestartFetch(prevState => prevState + 1)
          Swal.fire({
            title: translate('notifications.titles.success'),
            text: decoratePopUpMessage(`${error.message as string}. The zip download can take a few minutes depending on the size. We will send you an email once it is ready and available to download.`),
            imageUrl: iconNotifySuccess,
          });
        } else {
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(error.message as string),
            imageUrl: errorBigIcon,
          });
        }
      });
  }, []);

  useEffect(() => {
    !loading && gitlabActions && setRows(gitlabActions?.map((props, index) => ({...props, id: index})));
  }, [gitlabActions, loading]);

  useEffect(() => {
    if (!id) {
      navigate('/')
      return;
    }

    updList()
  }, [id]);


  useEffect(() => {
    return () => {
      dispatch(clearBackupsState());
    };
  }, [dispatch]);

  const updList = async (newPage?: number) => {
    try {
      if (!id) {
        return;
      }

      setIsLoading(true)
      await dispatch(getGitlabActions({itemId: id, perPage: 10, page: newPage || 0}))
        .unwrap()
        .catch((err) => {
          console.log(err);
          const error = err as IError;
          setIsLoading(false)

          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(error.error as string),
            imageUrl: errorBigIcon,
          })
            .then(() => {
              navigate('/platform/dashboard/gitlab')
            });
        });

      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      console.log(e);
    }
  }

  return (
    <PlatformActionsPageBlock
      platformName={platformName}
      rows={rows || []}
      downloadZipCallback={downloadZipCallback}
      restoreItemCallback={restoreItem}
      closeTrialPopupCallback={closeTrialPoppup}
      loading={loading}
      total={total}
      page={page}
      setPage={(p) => {
        setPage(p)
        updList(p)
      }}
      isLoading={isLoading}
      paymentPopup={paymentPoppup}
      paymentRequiredState={paymentRequiredState}
      canRestore={canRestore}
      isAdmin={isAdminRole}
    />
  );
};

export default withRedirectAfterLogout()(GitlabActivityPage);
